import React, { useState } from 'react';
import { Outlet, useParams } from 'react-router';
import AppLayoutToolbar from '@amzn/awsui-components-console/app-layout-toolbar';
import BreadcrumbGroup from '@amzn/awsui-components-console/breadcrumb-group';
import AppHeader from 'src/common/ui/AppHeader';
import AppNavContent from 'src/common/ui/AppNavContent';
import NamespaceSideNav from 'src/common/ui/NamespaceSideNav';
import FooterBar from 'src/common/ui/FooterBar';
import { useResizable } from 'src/common/hooks/useResizable';
import styles from './AppShell.module.scss';

const NAV_WIDTH_MIN = 240;
const NAV_WIDTH_MAX = 480;
const NAV_WIDTH_DEFAULT = NAV_WIDTH_MIN;

/**
 * Persistent application shell rendered as a React Router layout route.
 * Stays mounted across all child route navigations, preserving navigation
 * open/close state and avoiding chrome flicker.
 *
 * Child page content is rendered via <Outlet />.
 */
const AppShell: React.FC = () => {
  const { namespaceId } = useParams<{ namespaceId: string }>();
  const [navigationOpen, setNavigationOpen] = useState(false);

  const { width: navigationWidth, handleProps } = useResizable({
    initialWidth: NAV_WIDTH_DEFAULT,
    minWidth: NAV_WIDTH_MIN,
    maxWidth: NAV_WIDTH_MAX,
  });

  const navigationContent = (
    <div className={styles.navWrapper}>
      <NamespaceSideNav activeNamespace={namespaceId} />
      <div
        className={styles.resizeHandle}
        {...handleProps}
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={navigationWidth}
        aria-valuemin={NAV_WIDTH_MIN}
        aria-valuemax={NAV_WIDTH_MAX}
        aria-label="Resize navigation"
        tabIndex={0}
      />
    </div>
  );

  return (
    <>
      <AppHeader navContent={<AppNavContent />} />
      <AppLayoutToolbar
        contentType="default"
        toolsHide={true}
        headerSelector=".langhub-app-header"
        navigationWidth={navigationWidth}
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        navigation={navigationContent}
        ariaLabels={{
          navigation: 'My Namespaces',
          navigationClose: 'Close navigation',
          navigationToggle: 'Open My Namespaces',
        }}
        breadcrumbs={
          <BreadcrumbGroup items={[{ text: 'My Namespaces', href: '#' }]} />
        }
        content={<Outlet />}
      />
      <FooterBar />
    </>
  );
};

export default AppShell;
